import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, ChevronLeft, Calendar, FileEdit, ChevronDown, ChevronRight, Users, Paperclip, UserCheck, X, FileText, Clock, Ban, RotateCcw } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { useRef } from 'react';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { getInitials } from '../lib/people';
import { FileUpload } from '../components/FileUpload';
import { FollowUpList, type FollowUpTask } from '../components/FollowUpList';
import { useToast } from '../contexts/ToastContext';
import { formatRelativeDate, useNow } from '../lib/relativeTime';
import { getLocalToday } from '../lib/dates';
import { TimeSelect } from '../components/TimeSelect';
import { PinButton } from '../components/PinButton';

interface Person {
  id: string;
  name: string;
  email: string | null;
}

interface SchedulingConfig {
  day_of_week?: number;       // 0=Sun..6=Sat (legacy)
  days_of_week?: number[];    // multi-day weekly
  day_of_month?: number;      // 1-31
  monthly_ordinal?: number;   // 1-4 or -1 (last)
  monthly_weekday?: number;   // 0=Sun..6=Sat
  default_time?: string;      // HH:MM
  default_duration?: number;  // minutes
}

interface MeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  template_content: string | null;
  scheduling_config: string | null;
  end_date: string | null;
  default_attendee_ids?: string[];
}

interface MeetingInstance {
  id: string;
  series_id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  summary: string | null;
  created_at: string;
}

export function MeetingSeriesPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const toast = useToast();
  useNow(); // tick every 30s so relative dates stay fresh
  const [series, setSeries] = useState<MeetingSeries | null>(null);
  const [instances, setInstances] = useState<MeetingInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateLastSaved, setTemplateLastSaved] = useState<string | null>(null);
  const [defaultAttendeesOpen, setDefaultAttendeesOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [followUpsOpen, setFollowUpsOpen] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUpTask[]>([]);
  const [followUpRefreshCount, setFollowUpRefreshCount] = useState(0);
  const [allPeople, setAllPeople] = useState<Person[]>([]);
  const [showAddDefault, setShowAddDefault] = useState(false);
  const [defaultSearch, setDefaultSearch] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [builtInTemplates, setBuiltInTemplates] = useState<Array<{ id: string; name: string; description: string; content: object }>>([]);
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const addDefaultRef = useRef<HTMLDivElement>(null);
  const defaultInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiGet<Person[]>('/people').then(setAllPeople).catch(console.error);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addDefaultRef.current && !addDefaultRef.current.contains(e.target as Node)) {
        setShowAddDefault(false);
        setDefaultSearch('');
      }
    }
    if (showAddDefault) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showAddDefault]);

  useEffect(() => {
    if (showAddDefault && defaultInputRef.current) {
      defaultInputRef.current.focus();
    }
  }, [showAddDefault]);

  async function addDefaultAttendee(personId: string) {
    if (!series) return;
    const oldIds = series.default_attendee_ids ?? [];
    const newIds = [...oldIds, personId];
    setSeries({ ...series, default_attendee_ids: newIds });
    setShowAddDefault(false);
    setDefaultSearch('');
    try {
      const updated = await apiPatch<MeetingSeries & { default_attendee_ids: string[] }>(
        `/meeting-series/${seriesId}`, { default_attendee_ids: newIds }
      );
      setSeries(prev => prev ? { ...prev, default_attendee_ids: updated.default_attendee_ids ?? newIds } : prev);
    } catch (err) {
      console.error('Failed to update default attendees:', err);
      setSeries(prev => prev ? { ...prev, default_attendee_ids: oldIds } : prev);
      toast.error('Failed to update default attendees');
    }
  }

  async function removeDefaultAttendee(personId: string) {
    if (!series) return;
    const oldIds = series.default_attendee_ids ?? [];
    const newIds = oldIds.filter(id => id !== personId);
    setSeries({ ...series, default_attendee_ids: newIds });
    try {
      const updated = await apiPatch<MeetingSeries & { default_attendee_ids: string[] }>(
        `/meeting-series/${seriesId}`, { default_attendee_ids: newIds }
      );
      setSeries(prev => prev ? { ...prev, default_attendee_ids: updated.default_attendee_ids ?? newIds } : prev);
    } catch (err) {
      console.error('Failed to update default attendees:', err);
      setSeries(prev => prev ? { ...prev, default_attendee_ids: oldIds } : prev);
      toast.error('Failed to update default attendees');
    }
  }

  async function load() {
    if (!seriesId) return;
    try {
      const [s, i] = await Promise.all([
        apiGet<MeetingSeries>(`/meeting-series/${seriesId}`),
        apiGet<MeetingInstance[]>(`/meeting-series/${seriesId}/instances`),
      ]);
      setSeries({ ...s, default_attendee_ids: s.default_attendee_ids ?? [] });
      setInstances(i);
    } catch (err) {
      console.error('Failed to load meeting series:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [seriesId]);

  useEffect(() => {
    if (!seriesId) return;
    apiGet<FollowUpTask[]>(`/meeting-series/${seriesId}/follow-ups`)
      .then(setFollowUps)
      .catch(console.error);
  }, [seriesId, followUpRefreshCount]);

  const handleTemplateUpdate = useCallback(async (content: JSONContent) => {
    if (!seriesId) return;
    setTemplateSaving(true);
    try {
      await apiPatch(`/meeting-series/${seriesId}`, {
        template_content: JSON.stringify(content),
      });
      setTemplateLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  }, [seriesId]);

  function getSchedulingConfig(): SchedulingConfig {
    if (!series?.scheduling_config) return {};
    try { return JSON.parse(series.scheduling_config); } catch { return {}; }
  }

  async function saveSchedulingConfig(update: Partial<SchedulingConfig>) {
    if (!seriesId || !series) return;
    const current = getSchedulingConfig();
    const newConfig = { ...current, ...update };
    // Remove undefined/null keys
    for (const key of Object.keys(newConfig) as (keyof SchedulingConfig)[]) {
      if (newConfig[key] === undefined || newConfig[key] === null || newConfig[key] === '') {
        delete newConfig[key];
      }
    }
    const configStr = Object.keys(newConfig).length > 0 ? JSON.stringify(newConfig) : null;
    setSeries({ ...series, scheduling_config: configStr });
    try {
      await apiPatch(`/meeting-series/${seriesId}`, { scheduling_config: newConfig });
      toast.success('Scheduling updated');
    } catch (err) {
      console.error('Failed to update scheduling config:', err);
      toast.error('Failed to update scheduling');
      setSeries(prev => prev ? { ...prev, scheduling_config: series.scheduling_config } : prev);
    }
  }

  async function handleCreateInstance(date: string, title: string, time: string, duration?: number) {
    if (!seriesId) return;
    try {
      await apiPost(`/meeting-series/${seriesId}/instances`, {
        meeting_date: date,
        title: title || null,
        meeting_time: time || null,
        duration_minutes: duration || null,
      });
      setShowNewModal(false);
      toast.success('Meeting created');
      load();
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!series) {
    return <div className="h-full overflow-auto p-3 sm:p-6"><div className="text-text-muted text-center py-16">Meeting series not found.</div></div>;
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/meetings" className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-3">
          <ChevronLeft size={16} /> Back to Meetings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">{series.name}</h1>
            {series.description && (
              <p className="text-sm text-text-muted mt-1">{series.description}</p>
            )}
            {series.cadence && (
              <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-bg-input rounded-full text-text-secondary capitalize">
                {series.cadence}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <PinButton entityType="meeting_series" entityId={seriesId!} label={series.name} />
            {!series.end_date && series.cadence && series.cadence !== 'adhoc' && (
              <button
                onClick={async () => {
                  if (!window.confirm('End this series? No new meetings will be suggested. Existing meetings are kept.')) return;
                  const today = getLocalToday();
                  try {
                    const updated = await apiPatch<MeetingSeries>(`/meeting-series/${seriesId}`, { end_date: today });
                    setSeries(prev => prev ? { ...prev, end_date: updated.end_date } : prev);
                    toast.success('Series ended');
                  } catch {
                    toast.error('Failed to end series');
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-text-secondary text-sm font-medium hover:bg-bg-hover transition-colors"
              >
                <Ban size={14} />
                End Series
              </button>
            )}
            <button
              onClick={() => setShowNewModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
            >
              <Plus size={16} />
              New Meeting
            </button>
          </div>
        </div>
      </div>

      {/* Ended banner */}
      {series.end_date && (
        <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-lg bg-warning/10 border border-warning/20">
          <Ban size={16} className="text-warning flex-shrink-0" />
          <span className="text-sm text-text-secondary flex-1">
            This series ended on {formatDate(series.end_date)}. No new meetings will be suggested.
          </span>
          <button
            onClick={async () => {
              try {
                const updated = await apiPatch<MeetingSeries>(`/meeting-series/${seriesId}`, { end_date: null });
                setSeries(prev => prev ? { ...prev, end_date: updated.end_date } : prev);
                toast.success('Series reactivated');
              } catch {
                toast.error('Failed to reactivate series');
              }
            }}
            className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors flex-shrink-0"
          >
            <RotateCcw size={12} /> Reactivate
          </button>
        </div>
      )}

      {/* Template Editor */}
      <div className="mb-6 border border-border rounded-lg overflow-hidden">
        <button
          onClick={() => setTemplateOpen(!templateOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {templateOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <FileEdit size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Meeting Template</span>
          {templateSaving && <span className="text-xs text-accent">Saving...</span>}
          {templateLastSaved && !templateSaving && (
            <span className="text-xs text-text-muted">Saved at {templateLastSaved}</span>
          )}
        </button>
        {templateOpen && (
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-text-muted">
                This template is used as the starting content for new meetings in this series.
              </p>
              <div className="relative">
                <button
                  onClick={() => {
                    if (builtInTemplates.length === 0) {
                      apiGet<Array<{ id: string; name: string; description: string; content: object }>>('/meeting-templates')
                        .then(t => { setBuiltInTemplates(t); setShowTemplatePicker(true); })
                        .catch(console.error);
                    } else {
                      setShowTemplatePicker(!showTemplatePicker);
                    }
                  }}
                  className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors"
                >
                  <FileText size={12} /> Load Template
                </button>
                {showTemplatePicker && builtInTemplates.length > 0 && (
                  <div className="absolute right-0 top-6 z-20 w-56 bg-bg-surface border border-border rounded-lg shadow-lg py-1">
                    {builtInTemplates.filter(t => t.id !== 'blank').map(tmpl => (
                      <button
                        key={tmpl.id}
                        onClick={async () => {
                          try {
                            await apiPatch(`/meeting-series/${seriesId}`, {
                              template_content: JSON.stringify(tmpl.content),
                            });
                            setShowTemplatePicker(false);
                            toast.success(`Loaded "${tmpl.name}" template`);
                            load(); // reload to show new template content
                          } catch (err) {
                            console.error('Failed to load template:', err);
                            toast.error('Failed to load template');
                          }
                        }}
                        className="w-full text-left px-3 py-1.5 hover:bg-bg-hover transition-colors"
                      >
                        <span className="text-xs font-medium text-text-primary block">{tmpl.name}</span>
                        <span className="text-[10px] text-text-muted block">{tmpl.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="template-editor">
              <DossierEditor
                content={series.template_content ? (() => { try { return JSON.parse(series.template_content); } catch { return null; } })() : null}
                onUpdate={handleTemplateUpdate}
                editable={true}
                placeholder="Set up a template for new meetings (e.g., agenda sections, standard topics)..."
              />
            </div>
          </div>
        )}
      </div>

      {/* Default Attendees */}
      <div className="mb-6 border border-border rounded-lg">
        <button
          onClick={() => setDefaultAttendeesOpen(!defaultAttendeesOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {defaultAttendeesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Users size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Default Attendees</span>
          {(series.default_attendee_ids ?? []).length > 0 && (
            <span className="text-xs text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
              {(series.default_attendee_ids ?? []).length}
            </span>
          )}
        </button>
        {defaultAttendeesOpen && (
          <div className="p-4 border-t border-border">
            <p className="text-xs text-text-muted mb-3">
              People who normally attend. They'll be pre-checked on new meetings.
            </p>
            <div className="space-y-1.5">
              {(series.default_attendee_ids ?? []).map(id => {
                const person = allPeople.find(p => p.id === id);
                if (!person) return null;
                return (
                  <div key={id} className="flex items-center gap-2 group py-1 px-1 rounded hover:bg-bg-hover transition-colors">
                    <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {getInitials(person.name)}
                    </div>
                    <span className="text-sm text-text-primary truncate flex-1">{person.name}</span>
                    <button
                      onClick={() => removeDefaultAttendee(person.id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-text-muted hover:text-danger transition-all flex-shrink-0"
                      title="Remove default attendee"
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}

              <div className="relative" ref={addDefaultRef}>
                {showAddDefault ? (
                  <div>
                    <input
                      ref={defaultInputRef}
                      type="text"
                      value={defaultSearch}
                      onChange={e => setDefaultSearch(e.target.value)}
                      placeholder="Search people..."
                      className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                      onKeyDown={e => {
                        if (e.key === 'Escape') {
                          setShowAddDefault(false);
                          setDefaultSearch('');
                        }
                      }}
                    />
                    {(() => {
                      const available = allPeople.filter(
                        p => !(series.default_attendee_ids ?? []).includes(p.id) &&
                          (defaultSearch === '' || p.name.toLowerCase().includes(defaultSearch.toLowerCase()))
                      );
                      return (
                        <>
                          {available.length > 0 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-36 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg">
                              {available.map(person => (
                                <button
                                  key={person.id}
                                  onClick={() => addDefaultAttendee(person.id)}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-bg-hover transition-colors"
                                >
                                  <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
                                    {getInitials(person.name)}
                                  </div>
                                  <span className="text-xs text-text-primary truncate">{person.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {available.length === 0 && defaultSearch && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-bg-surface border border-border rounded-lg shadow-lg px-3 py-2">
                              <span className="text-xs text-text-muted">No matching people</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddDefault(true)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1"
                  >
                    <Plus size={12} />
                    Add attendee
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scheduling */}
      <div className="mb-6 border border-border rounded-lg">
        <button
          onClick={() => setSchedulingOpen(!schedulingOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {schedulingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Clock size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Scheduling</span>
          {(() => {
            const config = getSchedulingConfig();
            const parts: string[] = [];
            if (config.default_time) parts.push(formatTime12(config.default_time));
            if (config.default_duration) parts.push(`${config.default_duration}m`);
            if (config.days_of_week?.length) {
              parts.push(config.days_of_week.map(d => DAY_SHORT[d]).join(', '));
            } else if (config.day_of_week !== undefined) {
              parts.push(DAY_NAMES[config.day_of_week]);
            }
            if (config.monthly_ordinal !== undefined && config.monthly_weekday !== undefined) {
              parts.push(`${ORDINAL_LABELS[config.monthly_ordinal] ?? ''} ${DAY_NAMES[config.monthly_weekday]}`);
            } else if (config.day_of_month !== undefined) {
              parts.push(`${config.day_of_month}${getOrdinalSuffix(config.day_of_month)}`);
            }
            return parts.length > 0 ? (
              <span className="text-xs text-text-muted">{parts.join(' · ')}</span>
            ) : null;
          })()}
        </button>
        {schedulingOpen && (
          <div className="p-4 border-t border-border">
            <p className="text-xs text-text-muted mb-3">
              Configure default time and preferred day for suggested meetings.
            </p>
            <div className="space-y-3">
              {/* Default Time */}
              <div className="flex items-center gap-3">
                <label className="text-xs text-text-secondary w-24 flex-shrink-0">Default Time</label>
                <TimeSelect
                  value={getSchedulingConfig().default_time ?? ''}
                  onChange={(v) => saveSchedulingConfig({ default_time: v || undefined })}
                  className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm w-36"
                  placeholder="Not set"
                />
              </div>

              {/* Default Duration */}
              <div className="flex items-center gap-3">
                <label className="text-xs text-text-secondary w-24 flex-shrink-0">Duration</label>
                <select
                  value={getSchedulingConfig().default_duration ?? ''}
                  onChange={(e) => saveSchedulingConfig({ default_duration: e.target.value ? Number(e.target.value) : undefined })}
                  className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm w-36"
                >
                  <option value="">Not set</option>
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>

              {/* End Date */}
              <div className="flex items-center gap-3">
                <label className="text-xs text-text-secondary w-24 flex-shrink-0">End Date</label>
                <input
                  type="date"
                  value={series.end_date ?? ''}
                  onChange={async (e) => {
                    const val = e.target.value || null;
                    setSeries(prev => prev ? { ...prev, end_date: val } : prev);
                    try {
                      await apiPatch(`/meeting-series/${seriesId}`, { end_date: val });
                      toast.success(val ? 'End date set' : 'End date cleared');
                    } catch {
                      toast.error('Failed to update end date');
                      setSeries(prev => prev ? { ...prev, end_date: series.end_date } : prev);
                    }
                  }}
                  className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm w-36"
                />
                {series.end_date && (
                  <button
                    onClick={async () => {
                      setSeries(prev => prev ? { ...prev, end_date: null } : prev);
                      try {
                        await apiPatch(`/meeting-series/${seriesId}`, { end_date: null });
                        toast.success('End date cleared');
                      } catch {
                        toast.error('Failed to clear end date');
                        setSeries(prev => prev ? { ...prev, end_date: series.end_date } : prev);
                      }
                    }}
                    className="text-xs text-text-muted hover:text-danger transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Days of Week — for weekly/biweekly (multi-select chips) */}
              {(series.cadence === 'weekly' || series.cadence === 'biweekly') && (
                <div className="flex items-start gap-3">
                  <label className="text-xs text-text-secondary w-24 flex-shrink-0 mt-1">Days</label>
                  <div className="flex flex-wrap gap-1">
                    {DAY_SHORT.map((label, i) => {
                      const config = getSchedulingConfig();
                      const selected = config.days_of_week?.includes(i) ?? config.day_of_week === i;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            const current = config.days_of_week ?? (config.day_of_week !== undefined ? [config.day_of_week] : []);
                            const next = selected ? current.filter(d => d !== i) : [...current, i].sort();
                            saveSchedulingConfig({ days_of_week: next.length > 0 ? next : undefined, day_of_week: undefined });
                          }}
                          className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                            selected
                              ? 'bg-accent/20 border-accent text-accent font-medium'
                              : 'bg-bg-input border-border text-text-muted hover:border-border-hover'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Monthly pattern — for monthly/quarterly */}
              {(series.cadence === 'monthly' || series.cadence === 'quarterly') && (() => {
                const config = getSchedulingConfig();
                const hasOrdinal = config.monthly_ordinal !== undefined;
                const mode = hasOrdinal ? 'ordinal' : (config.day_of_month !== undefined ? 'date' : '');
                return (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <label className="text-xs text-text-secondary w-24 flex-shrink-0">Repeat on</label>
                      <select
                        value={mode}
                        onChange={(e) => {
                          if (e.target.value === 'date') {
                            saveSchedulingConfig({ day_of_month: 1, monthly_ordinal: undefined, monthly_weekday: undefined });
                          } else if (e.target.value === 'ordinal') {
                            saveSchedulingConfig({ day_of_month: undefined, monthly_ordinal: 1, monthly_weekday: 1 });
                          } else {
                            saveSchedulingConfig({ day_of_month: undefined, monthly_ordinal: undefined, monthly_weekday: undefined });
                          }
                        }}
                        className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm flex-1"
                      >
                        <option value="">Auto</option>
                        <option value="date">Day of month</option>
                        <option value="ordinal">Day of week</option>
                      </select>
                    </div>
                    {mode === 'date' && (
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-text-secondary w-24 flex-shrink-0">Day</label>
                        <select
                          value={config.day_of_month ?? 1}
                          onChange={(e) => saveSchedulingConfig({ day_of_month: Number(e.target.value) })}
                          className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm w-36"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>{d}{getOrdinalSuffix(d)}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {mode === 'ordinal' && (
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-text-secondary w-24 flex-shrink-0">Which</label>
                        <div className="flex gap-2 flex-1">
                          <select
                            value={config.monthly_ordinal ?? 1}
                            onChange={(e) => saveSchedulingConfig({ monthly_ordinal: Number(e.target.value) })}
                            className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm flex-1"
                          >
                            <option value={1}>1st</option>
                            <option value={2}>2nd</option>
                            <option value={3}>3rd</option>
                            <option value={4}>4th</option>
                            <option value={-1}>Last</option>
                          </select>
                          <select
                            value={config.monthly_weekday ?? 1}
                            onChange={(e) => saveSchedulingConfig({ monthly_weekday: Number(e.target.value) })}
                            className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm flex-1"
                          >
                            {DAY_NAMES.map((name, i) => (
                              <option key={i} value={i}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {!series.cadence || series.cadence === 'adhoc' ? (
                <p className="text-[10px] text-text-muted italic">Set a cadence to enable scheduling options.</p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="mb-6 border border-border rounded-lg">
        <button
          onClick={() => setAttachmentsOpen(!attachmentsOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {attachmentsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Paperclip size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Attachments</span>
          {attachmentCount > 0 && (
            <span className="text-xs text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
              {attachmentCount}
            </span>
          )}
        </button>
        {attachmentsOpen && (
          <div className="p-4 border-t border-border">
            <p className="text-xs text-text-muted mb-3">
              Files shared across all meetings in this series.
            </p>
            <FileUpload
              parentType="meeting_series"
              parentId={seriesId!}
              onCountChange={setAttachmentCount}
            />
          </div>
        )}
      </div>

      {/* Follow-ups */}
      <div className="mb-6 border border-border rounded-lg">
        <button
          onClick={() => setFollowUpsOpen(!followUpsOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {followUpsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <UserCheck size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Follow-ups</span>
          {followUps.length > 0 && (
            <span className="text-xs text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
              {followUps.length}
            </span>
          )}
        </button>
        {followUpsOpen && (
          <div className="p-4 border-t border-border">
            <FollowUpList
              followUps={followUps}
              showMeetingDate
              onStatusToggle={() => setFollowUpRefreshCount(c => c + 1)}
            />
          </div>
        )}
      </div>

      {instances.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Calendar size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No meetings yet</p>
          <p className="text-sm">Create a new meeting instance to start taking notes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {instances.map((inst) => (
            <Link
              key={inst.id}
              to={`/meetings/${series.id}/${inst.id}`}
              className="block bg-bg-card border border-border rounded-lg p-4 hover:border-border-hover transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                    {inst.title || formatDate(inst.meeting_date)}
                    {inst.meeting_time && (() => {
                      const dur = inst.duration_minutes ?? getSchedulingConfig().default_duration;
                      return (
                        <span className="text-xs font-normal text-text-muted ml-2">
                          {formatTime12(inst.meeting_time)}
                          {dur ? ` · ${formatDuration(dur)}` : ''}
                        </span>
                      );
                    })()}
                  </h3>
                  {inst.title && (
                    <p className="text-xs text-text-muted mt-0.5">
                      {formatDate(inst.meeting_date)}
                      {!inst.title && inst.meeting_time ? '' : ''}
                    </p>
                  )}
                  {inst.summary && (
                    <p className="text-xs text-text-muted mt-1 truncate max-w-lg">{inst.summary}</p>
                  )}
                </div>
                <span className="text-xs text-text-muted">{formatRelativeDate(inst.meeting_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showNewModal && (
        <NewInstanceModal
          seriesName={series.name}
          onSave={handleCreateInstance}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </div>
    </div>
  );
}

function NewInstanceModal({
  seriesName,
  onSave,
  onClose,
}: {
  seriesName: string;
  onSave: (date: string, title: string, time: string, duration?: number) => void;
  onClose: () => void;
}) {
  const today = getLocalToday();
  const [date, setDate] = useState(today);
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-1">New Meeting</h2>
        <p className="text-sm text-text-muted mb-4">{seriesName}</p>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Time</label>
              <TimeSelect
                value={time}
                onChange={setTime}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Duration</label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              >
                <option value="">Default</option>
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g., ${seriesName} — ${formatDate(date)}`}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors">
            Cancel
          </button>
          <button
            onClick={() => date && onSave(date, title, time, duration ? Number(duration) : undefined)}
            disabled={!date}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDINAL_LABELS: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', '-1': 'Last' };

function getOrdinalSuffix(n: number): string {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatTime12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}


